import { Button, Card, CheckboxField, Flex, Link } from "@aws-amplify/ui-react"
import type { MachineSupaDto } from "@strtmn/sds/types"
import { formatUnicorn, getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useContext, useState, type PropsWithChildren } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import {
  FaDownload,
  FaRegBell,
  FaRegFilePdf,
  FaRegSquareCheck,
  FaShare,
  FaShareNodes,
} from "react-icons/fa6"
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
} from "react-router-dom"
import { acceptTerms, cancelTerms } from "../../api/EuDataActApi"
import { getLatestActiveDnv } from "../../api/MachinesApi"
import { ErrorBanner, LoadingErrorState } from "../../components"
import { DataActConsent } from "../../components/eu-data-act/DataActConsent"
import { OneTimeSuccessMessage } from "../../components/OneTimeSuccessMessage"
import VehiclePageLayout from "../../components/VehiclePageLayout"
import { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId, useCurrentTerms } from "../../hooks"
import {
  getLinkToEuDataActDataSharing,
  getLinkToEuDataActTerms,
  getLinkToEuDataActTransfer,
  getLinkToVehicleDataDownloadOverview,
} from "../../LinkUtils"

interface PageWrapperProps extends PropsWithChildren {
  vin: string
}
function PageWrapperCardsOneColumn({ vin, children }: PageWrapperProps) {
  return (
    <VehiclePageLayout vin={vin}>
      <h2>EU Data Act</h2>

      <Flex direction="column">{children}</Flex>
    </VehiclePageLayout>
  )
}

type FormValuesCancel = {
  iHaveReadTheDrawbacks: boolean
}
interface HasActiveDnvBlockProps {
  machine: MachineSupaDto
}
function HasActiveDnvBlock({ machine }: HasActiveDnvBlockProps) {
  const navigate = useNavigate()
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<FormValuesCancel>()
  const onSubmit: SubmitHandler<FormValuesCancel> = async () => {
    console.log("Form CANCEL submitted")
    setIsFormSubmitting(true)
    try {
      await cancelTerms(machine.serial_number)
      navigate(0)
    } catch (error) {
      console.error("Failed to save:", error)
      const eAsAny = error as any
      setError("root.serverError", {
        type: eAsAny.statusCode,
        message: isProd
          ? I18n.get("Fehler beim Speichern.")
          : I18n.get("Fehler beim Speichern.") +
            " " +
            getErrorMessage(eAsAny.body),
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  return (
    <>
      <Card>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors?.root && errors?.root["serverError"] && (
            <p className="error">{errors.root["serverError"].message}</p>
          )}
          <div className="mb-2">
            {formatUnicorn(
              I18n.get(
                "Wenn Sie die Datennutzungsvereinbarung kündigen, wird die Datenübertragung von der Maschine {machineSerial} unterbrochen."
              ),
              {
                machineSerial: machine.serial_number,
              }
            )}
            <ul>
              <li>
                <Flex alignItems="center" gap="xs">
                  ❌{" "}
                  {I18n.get(
                    "Sie erhalten keine Wiegedaten, Fütterungsdaten und Rezeptauswertungen mehr."
                  )}
                </Flex>
              </li>
              <li>
                <Flex alignItems="center" gap="xs">
                  ❌{" "}
                  {I18n.get(
                    "Der BSL Kundendienst und Ihr Händler haben keinen Zugriff mehr auf die Maschinendaten. Bei Problemen kann sich deren Behebung dadurch verzögern."
                  )}
                </Flex>
              </li>
              {/*
          ... Modal: {I18n.get("Sind sie sicher? Das hier wird passieren: ...")}
          */}
              {!isProd && (
                <li>
                  <Flex alignItems="center" gap="xs">
                    📞 {I18n.get("Telefonnummer für Rückfragen:")} ...
                  </Flex>
                </li>
              )}
            </ul>
          </div>
          <div className="mb-2">
            <CheckboxField
              label={I18n.get("Zur Kenntnis genommen")}
              {...register("iHaveReadTheDrawbacks")}
            />
          </div>
          <div>
            <Button
              colorTheme="error"
              type="submit"
              isDisabled={!watch("iHaveReadTheDrawbacks") || isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Anfrage wird gesendet...")}
            >
              {formatUnicorn(
                I18n.get(
                  "Datennutzungsvereinbarung für Maschine {machineSerial} kündigen"
                ),
                {
                  machineSerial: machine.serial_number,
                }
              )}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}

type FormValuesAccept = {
  shareWithDealer: boolean
  iHaveReadTheTerms: boolean
}
interface HasNoActiveDnvBlockProps {
  currentTermsUrl: string
  currentTermsVersion: string
  machine: MachineSupaDto
}
function HasNoActiveDnvBlock({
  currentTermsUrl,
  currentTermsVersion,
  machine,
}: HasNoActiveDnvBlockProps) {
  const navigate = useNavigate()
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<FormValuesAccept>({
    // `false` while the dealer question is hidden outside dev: nobody is
    // asked, so nothing is consented to. Flip when the checkbox is shown.
    defaultValues: { shareWithDealer: false, iHaveReadTheTerms: false },
  })

  const onSubmit: SubmitHandler<FormValuesAccept> = async (data) => {
    console.debug("Form ACCEPT submitted with:", data)
    setIsFormSubmitting(true)
    try {
      await acceptTerms(
        machine.serial_number,
        currentTermsVersion,
        data.shareWithDealer
      )
      navigate(0)
    } catch (error) {
      console.error("Failed to save:", error)
      const eAsAny = error as any
      setError("root.serverError", {
        type: eAsAny.statusCode,
        message: isProd
          ? I18n.get("Fehler beim Speichern.")
          : I18n.get("Fehler beim Speichern.") +
            " " +
            getErrorMessage(eAsAny.body),
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  return (
    <>
      <Flex className="error !p-2 mb-4" alignItems="center" gap="xs">
        {/* other options for the icon: Flag, */}
        <FaRegBell className="inline mr-2" />
        <div>
          {formatUnicorn(
            I18n.get(
              "Für die Maschine {machineSerial} ist keine aktive Datennutzungsvereinbarung abgeschlossen."
            ),
            { machineSerial: machine.serial_number }
          )}
        </div>
        <br />
      </Flex>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors?.root && errors?.root["serverError"] && (
            <p className="error">{errors.root["serverError"].message}</p>
          )}
          <DataActConsent
            currentTermsVersion={currentTermsVersion}
            currentTermsUrl={currentTermsUrl}
            machineSerial={machine.serial_number}
            shareWithDealer={register("shareWithDealer")}
            acknowledge={register("iHaveReadTheTerms")}
          />
          <div>
            <Button
              variation="primary"
              type="submit"
              isDisabled={!watch("iHaveReadTheTerms") || isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Anfrage wird gesendet...")}
            >
              {formatUnicorn(
                "Ich stimme der Datennutzungsvereinbarung (Version {currentTermsVersion}) für die Maschine {machineSerial} zu.",
                {
                  currentTermsVersion,
                  machineSerial: machine.serial_number,
                }
              )}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}

function EuDataActPage() {
  // const navigate = useNavigate()
  const userPrefLang = "de" // useUserPrefLang()
  const params = useParams<{
    vin: string | undefined
  }>()
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  const terms = useCurrentTerms(userPrefLang)

  const vin = params.vin!
  let machineFromUserCtx = undefined
  if (activeOrgId && userData) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    !isProd &&
      console.debug(
        `${machinesFromUserCtx.length} machines from userData:`,
        machinesFromUserCtx
      )
    machineFromUserCtx = machinesFromUserCtx.find(
      (supa) => supa.serial_number === vin
    )
  }

  if (terms.isLoading || terms.error) {
    return (
      <PageWrapperCardsOneColumn vin={vin!}>
        <LoadingErrorState
          isLoading={terms.isLoading}
          loadingMessage={I18n.get("Lade Maschinendaten, bitte warten...")}
          isError={!!terms.error}
          errorMessage={`${I18n.get("Fehler")}: ${I18n.get(terms.error!)}`}
        />
      </PageWrapperCardsOneColumn>
    )
  }
  if (!machineFromUserCtx) {
    return (
      <PageWrapperCardsOneColumn vin={vin!}>
        <ErrorBanner
          error={`${I18n.get("Fehler")}: ${I18n.get("Keine Maschinendaten geladen.")}`}
        />
      </PageWrapperCardsOneColumn>
    )
  }

  const loadedMachine: MachineSupaDto = machineFromUserCtx

  return (
    <PageWrapperCardsOneColumn vin={vin!}>
      <OneTimeSuccessMessage />
      {!loadedMachine.hasActiveDnv ? (
        /* Fall 1: Maschine hat keine aktive DNV */
        <div className="mt-4">
          {terms.termsVersion && terms.termsUrl ? (
            <HasNoActiveDnvBlock
              currentTermsUrl={terms.termsUrl}
              currentTermsVersion={terms.termsVersion}
              machine={loadedMachine}
            />
          ) : (
            <ErrorBanner
              error={`${I18n.get("Fehler")}: ${I18n.get("Datennutzungsvereinbarung nicht gefunden.")}`}
            />
          )}
        </div>
      ) : (
        /* Fall 2: Maschine hat aktive DNV */
        <Flex className="success !p-2 mb-4" alignItems="center" gap="xs">
          <FaRegSquareCheck />
          <div>
            {formatUnicorn(
              I18n.get(
                "Für die Maschine {machineSerial} ist eine aktive Datennutzungsvereinbarung abgeschlossen."
              ),
              {
                machineSerial: loadedMachine.serial_number,
              }
            )}
          </div>
        </Flex>
      )}

      <div className="mt-4">
        {I18n.get("Weitere Aktionen")}
        <Card>
          <ul>
            <li>
              <Link
                as={ReactRouterLink}
                to={getLinkToVehicleDataDownloadOverview({
                  vin: loadedMachine.serial_number,
                })}
              >
                <Flex alignItems="center" gap="xs">
                  <FaDownload />
                  {formatUnicorn(
                    I18n.get(
                      "Daten herunterladen, die durch Benutzung der Maschine {machineSerial} entstanden sind"
                    ),
                    { machineSerial: loadedMachine.serial_number }
                  )}
                </Flex>
              </Link>
            </li>

            {loadedMachine.hasActiveDnv && (
              // Show these links only for machines with active DNV.
              <>
                <li>
                  <Link
                    href={getLinkToEuDataActTerms(
                      getLatestActiveDnv(loadedMachine)?.terms_version!,
                      userPrefLang
                    )}
                    target="_blank"
                  >
                    <Flex alignItems="center" gap="xs">
                      <FaRegFilePdf />
                      {formatUnicorn(
                        I18n.get(
                          "Datennutzungsvereinbarung in Version {termsVersion} herunterladen und/oder einsehen"
                        ),
                        {
                          termsVersion:
                            getLatestActiveDnv(loadedMachine)?.terms_version,
                        }
                      )}
                    </Flex>
                  </Link>
                </li>
                <li>
                  <Link
                    as={ReactRouterLink}
                    to={getLinkToEuDataActDataSharing({
                      vin: loadedMachine.serial_number,
                    })}
                  >
                    <Flex alignItems="center" gap="xs">
                      <FaShareNodes />
                      {I18n.get("Weitergabe von Daten beantragen")}
                    </Flex>
                  </Link>
                </li>
                <li>
                  <Link
                    as={ReactRouterLink}
                    to={getLinkToEuDataActTransfer({
                      vin: loadedMachine.serial_number,
                    })}
                  >
                    <Flex alignItems="center" gap="xs">
                      <FaShare />
                      {I18n.get(
                        "Weiterverkauf / Weitergabe der Maschine melden"
                      )}
                    </Flex>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </Card>
      </div>

      {loadedMachine.hasActiveDnv && (
        <div className="mt-4">
          {/* Fall 2: Maschine hat aktive DNV */}
          <HasActiveDnvBlock machine={loadedMachine} />
        </div>
      )}
    </PageWrapperCardsOneColumn>
  )
}

export const Component = EuDataActPage
